import { t } from 'i18next';
import { OhpenValidationError } from '@pmp/api';
import { DeleteErrorException } from '@ohpen/enso-ui';

const firstString = (v: unknown): string | undefined =>
  typeof v === 'string' && v.trim().length > 0 ? v.trim() : undefined;

// ASP.NET ValidationProblemDetails: { errors: { field: ["msg", ...] } }
const flattenErrors = (errors: unknown): string | undefined => {
  if (!errors || typeof errors !== 'object') return undefined;
  const msgs = Object.values(errors as Record<string, unknown>)
    .flat()
    .filter((m): m is string => typeof m === 'string');
  return msgs.length > 0 ? msgs.join(' ') : undefined;
};

// The @hey-api client wraps the thrown error; the problem-details body may sit under
// error/data/response/body. Search those wrappers (bounded) for the given field.
const findField = (error: unknown, key: string, depth = 0): string | undefined => {
  if (depth > 4 || !error || typeof error !== 'object') return undefined;
  const rec = error as Record<string, unknown>;
  const direct = firstString(rec[key]);
  if (direct) return direct;
  for (const nested of [rec.error, rec.data, rec.response, rec.body]) {
    const found = findField(nested, key, depth + 1);
    if (found) return found;
  }
  return undefined;
};

export const getApiErrorDetail = (error: unknown): string | undefined => {
  // @hey-api can throw the raw response text; parse a JSON body so we read `detail`
  // instead of dumping the whole problem+json blob into the dialog.
  if (typeof error === 'string') {
    const s = error.trim();
    if (!s) return undefined;
    if (s.startsWith('{') || s.startsWith('[')) {
      try {
        const parsed = JSON.parse(s);
        if (parsed && typeof parsed === 'object') return getApiErrorDetail(parsed);
      } catch { /* not JSON — fall through to the plain string */ }
    }
    return s;
  }
  const detail =
    findField(error, 'detail') ??
    flattenErrors((error as Record<string, unknown>)?.errors) ??
    findField(error, 'title');
  if (detail) return detail;
  // The client can also throw an Error whose message is the raw response text;
  // run it through the string branch instead of surfacing the JSON blob.
  const message = findField(error, 'message');
  return message === undefined ? undefined : getApiErrorDetail(message);
};

export const getApiErrorMessage = (error: unknown): string => getApiErrorDetail(error) ?? t('global.errors.unexpected');

export function createDeleteErrorException(error?: OhpenValidationError): DeleteErrorException {
  if (!error) {
    return new DeleteErrorException(t('global.errors.unexpected'));
  }
  const { title, status, errors } = error;
  let description: string | string[] = firstString(error.detail) ?? t('global.errors.unexpected');
  const invalidParams = error['invalid-params'];
  let message;

  if (title) message = title;
  if (status === 400 && invalidParams) {
    description = invalidParams.map(param => `${param.name}: ${param.reason}`);
  }
  if (errors && typeof errors === 'object') {
    description = Object.values(errors).flat();
  }

  return new DeleteErrorException(description, message);
}
